import React, { useCallback } from 'react';
import { ApiAlertAssignment, ApiAlertStatusEvent, ApiAssignmentUser, ApiBridgeAlert, ApiNote } from '../api';
import './AlertHistory.scss';
import {
  HistoryEvent,
  HistoryEventType,
  HistoryModalWithToggle,
} from '../common/alert-section/history/HistoryModalWithToggle';
import {
  InternalApiAlertAssignment,
  InternalApiAlertStatusEvent,
  InternalApiAlertType,
  InternalApiMonitoringAlert,
  InternalApiNote,
  InternalApiScreeningAlertStatus,
} from '../api-internal';
import { alertAssignmentInternalApi, alertStatusApi } from '../apis';
import { useAlertStatuses } from '../screening/alert-status/alertStatusesHelper';

interface Props {
  alert: ApiBridgeAlert;
  dropdownItem?: boolean;
}

export const AlertHistory = ({ alert, dropdownItem }: Props) => {
  const getHistoryEvents = (): Promise<HistoryEvent[]> => {
    const { assignments, statuses, notes } = alert;
    const events: HistoryEvent[] = [];
    assignments && events.push(...assignments.map((assignment) => toAssignmentEvent(assignment)));
    statuses && events.push(...statuses.map((status) => toStatusEvent(status, [])));
    notes && events.push(...notes.map((note) => toNoteEvent(note)));
    return Promise.resolve(events);
  };

  return (
    <HistoryModalWithToggle
      loadEvents={getHistoryEvents}
      dropdownItem={dropdownItem}
      alertId={alert.id}
      alertType={InternalApiAlertType.BRIDGE}
    />
  );
};

export const MonitoringAlertHistory = ({
  alert,
  dropdownItem,
}: {
  alert: InternalApiMonitoringAlert;
  dropdownItem?: boolean;
}) => {
  const [, existingStatuse] = useAlertStatuses();
  const getHistoryEvents = useCallback(async (): Promise<HistoryEvent[]> => {
    const { notes } = alert;
    const statuses = await fetchStatusEvents({ alertId: alert.id, alertType: InternalApiAlertType.MONITORING });
    const assignments = await fetchAssignments({ alertId: alert.id, alertType: InternalApiAlertType.MONITORING });
    const events: HistoryEvent[] = [];
    assignments && events.push(...assignments.map((assignment) => toAssignmentEvent(assignment)));
    statuses && events.push(...statuses.map((status) => toStatusEvent(status, existingStatuse)));
    notes && events.push(...notes.map((note) => toNoteEvent(note)));
    return events;
  }, [alert]);

  return (
    <HistoryModalWithToggle
      loadEvents={getHistoryEvents}
      dropdownItem={dropdownItem}
      alertId={alert.id}
      alertType={InternalApiAlertType.MONITORING}
    />
  );
};

interface LoadDataProps {
  alertId: number;
  alertType: InternalApiAlertType;
}

const fetchStatusEvents = async (props: LoadDataProps) => {
  return await alertStatusApi.getAllAlertStatusUpdates({
    alertId: props.alertId,
    alertType: props.alertType,
  });
};
const fetchAssignments = async (props: LoadDataProps) => {
  return await alertAssignmentInternalApi.getAllAssignments({ alertId: props.alertId, alertType: props.alertType });
};

const toAssignmentEvent = (assignment: ApiAlertAssignment | InternalApiAlertAssignment): HistoryEvent => ({
  type: HistoryEventType.ASSIGNMENT,
  startTime: assignment.startTime ?? new Date(),
  assigner: assignment.assigner,
  assignee: assignment.assignee,
});

const toStatusEvent = (
  status: ApiAlertStatusEvent | InternalApiAlertStatusEvent,
  existingStatuses: InternalApiScreeningAlertStatus[]
): HistoryEvent => ({
  type: HistoryEventType.STATUS,
  startTime: status.startTime ?? new Date(),
  assigner: status.assigner,
  status:
    'status' in status
      ? status.status
      : (existingStatuses.find((s) => s.id === status.statusId)?.textName ?? `Status with ID: ${status.statusId}`),
});

const toNoteEvent = (note: ApiNote | InternalApiNote): HistoryEvent => ({
  type: HistoryEventType.NOTE,
  startTime: note.createdTime || new Date(),
  assigner:
    note.user &&
    ({
      id: note.user.id,
      email: note.user.email,
      name: note.user.name,
    } as ApiAssignmentUser),
  comment: note.content,
});
